import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo, PyramidMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Sparkles, FileText } from "lucide-react";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (mode) => {
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ name, email, password, role: "Engagement Lead" });
      toast.success("Welcome to Yanthra Labs Business360");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 brand-gradient text-white overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#29abe2]/20 blur-3xl" />
        <div className="absolute -left-20 bottom-10 w-80 h-80 rounded-full bg-[#c9a227]/10 blur-3xl" />
        <Logo />
        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">
            Turn meetings into <span className="text-[#29abe2]">SAP-ready</span> deliverables.
          </h1>
          <p className="mt-4 text-white/70 text-sm leading-relaxed">
            Plan, record and transcribe requirement discussions, then generate branded BRDs, functional &
            technical specs, integration designs and a tracked project plan — all traceable to the transcript.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: Sparkles, t: "AI module mapping & pre-meeting questionnaires" },
              { icon: FileText, t: "Branded BRD / FSD / TSD / Integration Specs" },
              { icon: ShieldCheck, t: "DPDP-aligned, consent-captured recordings" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-white/85">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-white/10"><f.icon size={16} /></span>
                {f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/50">
          Yanthra Labs Private Limited · Chennai, Tamil Nadu · CIN U62013TN2025PTC178429
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8"><Logo onLight /></div>
          <h2 className="text-2xl font-semibold text-foreground">Yanthra Labs Business360</h2>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your consulting workspace.</p>

          <Button onClick={handleGoogle} variant="outline" data-testid="google-login-btn"
            className="w-full mt-6 h-11 gap-2 font-medium">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or use email <div className="h-px flex-1 bg-border" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="space-y-3 mt-4">
              <div><Label>Email</Label><Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" /></div>
              <div><Label>Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></div>
              <Button data-testid="login-submit" disabled={busy} onClick={() => submit("login")} className="w-full h-11 gap-2">
                Sign in <ArrowRight size={16} />
              </Button>
            </TabsContent>
            <TabsContent value="register" className="space-y-3 mt-4">
              <div><Label>Full name</Label><Input data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Consultant" /></div>
              <div><Label>Email</Label><Input data-testid="reg-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" /></div>
              <div><Label>Password</Label><Input data-testid="reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password" /></div>
              <Button data-testid="reg-submit" disabled={busy} onClick={() => submit("register")} className="w-full h-11 gap-2">
                Create account <ArrowRight size={16} />
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
